import { useCallback, useMemo, useState } from 'react';
import { Pressable, View, ViewProps } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { formatISO } from 'date-fns';
import { ConsumedDateRange } from '@/lib/local-api/meal/consumed-date-range';
import { MealSlotGrouper } from '@/lib/local-api/meal/meal-slot-grouper';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';
import { useCurrentOverviewDate } from '~/calendar/hooks/useCurrentOverviewDate';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { MealType, MealUtils } from '@fytness/shared';
import { useQuery } from '@apollo/client/react';
import { MealsByIdsDocument } from '~/graphql/generated/graphql';
import type { Meal } from '~/graphql/generated/graphql';
import { useColor } from '~/global/hooks/useColor';
import { useCurrentLayout } from '~/global/hooks/useCurrentLayout';
import { Copy as CopyIcon, CornerUpRight } from 'lucide-react-native';
import { Icon as UiIcon } from '@/components/ui/icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RowMenu } from '@/components/ui/row-menu/RowMenu';
import { useMealLogCopy } from '../meal-log-copy/useMealLogCopy';

export interface MealListOverviewSectionProps extends ViewProps {}

const MOBILE_MEAL_TYPE_ORDER: MealType[] = [
  MealType.BREAKFAST,
  MealType.LUNCH,
  MealType.SNACK,
  MealType.DINNER,
];

const LEFT_COLUMN_TYPES = [MealType.BREAKFAST, MealType.LUNCH];
const RIGHT_COLUMN_TYPES = [MealType.DINNER, MealType.SNACK];

const MEAL_TYPE_ICONS: Record<MealType, string> = {
  BREAKFAST: 'Sunrise',
  LUNCH: 'Sun',
  DINNER: 'Moon',
  SNACK: 'Apple',
};

type MealLog = {
  id: string;
  niceId: string | null;
  mealId: string;
  mealType: string | null;
  consumedAmount: number | null;
  consumedAmountUnit: string | null;
  consumedAt: string;
};

export const MealListOverviewSection = ({
  className,
  ...props
}: MealListOverviewSectionProps) => {
  const { t } = useTranslation('meal');
  const router = useRouter();
  const selectedDate = useCurrentOverviewDate();
  const layout = useCurrentLayout();
  const isMobile = layout === 'tabs';
  const accentColor = useColor('accent');
  const mutedColor = useColor('text-muted');
  const [expandedTypes, setExpandedTypes] = useState<Set<MealType>>(new Set());

  // The SQL bounds are a deliberate superset (widened a day each side); the
  // exact local-day decision is made in memory by `filterToDays`.
  const { fromDate, toDate } = ConsumedDateRange.forSingleDay(selectedDate);

  const { data: rawMealLogs = [] } = useLivePowerSyncQuery<MealLog>(
    db.MealLog.queries.findByDateRange(fromDate, toDate),
    [fromDate, toDate],
  );

  const mealLogs = useMemo(
    () =>
      ConsumedDateRange.filterToDays(rawMealLogs, selectedDate, selectedDate),
    [rawMealLogs, selectedDate],
  );

  const mealIds = useMemo(
    () =>
      Array.from(
        new Set((mealLogs ?? []).map((l) => l.mealId).filter(Boolean)),
      ),
    [mealLogs],
  );
  const { data: mealsData } = useQuery(MealsByIdsDocument, {
    variables: { ids: mealIds as string[] },
    skip: mealIds.length === 0,
  });
  const mealsById = useMemo(() => {
    const map = new Map<string, Meal>();
    (mealsData?.mealsByIds ?? []).forEach((m) => map.set(m.id, m as Meal));
    return map;
  }, [mealsData]);

  const mealsByType = useMemo(
    () => MealSlotGrouper.groupBySlot(mealLogs),
    [mealLogs],
  );

  const { dispatchMealLogCopy } = useMealLogCopy();

  const mealNamesById = useMemo(() => {
    const names: Record<string, string> = {};
    mealsById.forEach((meal, id) => {
      if (meal.name) names[id] = meal.name;
    });
    return names;
  }, [mealsById]);

  /**
   * Both overview tiers hand their **already-grouped** rows through as ids —
   * the grouping the section renders is the grouping the operation acts on, so
   * neither tier needs a selection step (DD1).
   */
  const dispatchForLogs = useCallback(
    (logs: MealLog[], mode: 'copy' | 'move') => {
      if (logs.length === 0) return;
      dispatchMealLogCopy({
        sourceIds: logs.map((log) => log.id),
        mode,
        defaultDate: selectedDate,
        mealNamesById,
      });
    },
    [dispatchMealLogCopy, selectedDate, mealNamesById],
  );

  const toggleExpanded = useCallback((type: MealType) => {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const handleCreateMeal = (mealType?: MealType) => {
    const dateStr = formatISO(selectedDate, { representation: 'date' });
    router.push({
      pathname: `/meals/${dateStr}`,
      params: mealType ? { type: mealType } : {},
    });
  };

  const getMealTypeNutrition = useCallback(
    (logs: MealLog[]) => {
      return logs.reduce(
        (acc, log) => {
          const meal = mealsById.get(log.mealId);
          if (!meal) return acc;
          const adj = MealUtils.adjustedNutrition(
            meal,
            log.consumedAmount ?? 0,
          );
          acc.kcal += adj.calories ?? 0;
          acc.carbs += adj.carbs ?? 0;
          acc.protein += adj.protein ?? 0;
          acc.fats += adj.fats ?? 0;
          return acc;
        },
        { kcal: 0, carbs: 0, protein: 0, fats: 0 },
      );
    },
    [mealsById],
  );

  const getMealNames = useCallback(
    (logs: MealLog[]) => {
      return logs
        .map((log) => mealsById.get(log.mealId)?.name)
        .filter(Boolean)
        .join(', ');
    },
    [mealsById],
  );

  const formatMacroShort = (nutrition: {
    carbs: number;
    protein: number;
    fats: number;
  }) =>
    t('macroShort', {
      carbs: Math.round(nutrition.carbs),
      protein: Math.round(nutrition.protein),
      fats: Math.round(nutrition.fats),
    });

  const MealSlot = ({
    mealType,
    showBorder = false,
  }: {
    mealType: MealType;
    showBorder?: boolean;
  }) => {
    const logs = mealsByType[mealType];
    const hasMeals = logs.length > 0;
    const isExpanded = expandedTypes.has(mealType);
    const icon = MEAL_TYPE_ICONS[mealType];
    const label = t(`mealType.${mealType.toLowerCase()}`, mealType);
    const nutrition = hasMeals ? getMealTypeNutrition(logs) : null;
    const subtitle = hasMeals ? getMealNames(logs) : t('empty.slotSubtitle');

    const headerClassName = 'flex flex-row items-center gap-3 p-4';
    const onHeaderPress = () =>
      hasMeals ? toggleExpanded(mealType) : handleCreateMeal(mealType);

    const headerContent = (
      <>
        <View
          className="flex items-center justify-center rounded-lg bg-surface-elevated"
          style={{ width: 40, height: 40 }}
        >
          <Icon
            name={icon as any}
            size={18}
            color={hasMeals ? accentColor : mutedColor}
          />
        </View>
        <View className="flex-1 gap-0.5">
          <Text className="font-inter-medium text-sm text-text-primary">
            {label}
          </Text>
          <Text
            className="font-inter-regular text-text-muted"
            style={{ fontSize: 12 }}
            numberOfLines={1}
          >
            {subtitle}
          </Text>
        </View>
        {hasMeals && nutrition ? (
          <View className="items-end gap-0.5">
            <Text className="font-sora-bold text-sm text-text-primary">
              {Math.round(nutrition.kcal)} {t('nutrition.kcal.unit')}
            </Text>
            <Text
              className="font-inter-medium text-text-muted"
              style={{ fontSize: 10 }}
            >
              {formatMacroShort(nutrition)}
            </Text>
          </View>
        ) : (
          <View className="rounded-md bg-surface-elevated px-3 py-1.5">
            <Text className="font-inter-medium text-xs text-text-muted">
              {t('addAction')}
            </Text>
          </View>
        )}
        <Icon
          name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
          size={16}
          color={mutedColor}
        />
      </>
    );

    return (
      // `collapsable={false}`: this is a layout-only View, which Fabric flattens away on
      // New Arch — taking the testID with it, so the native tree never sees it.
      <View
        testID={`meal-overview-slot-${mealType.toLowerCase()}`}
        collapsable={false}
        className={showBorder ? 'border-b border-border' : ''}
      >
        {/*
         * Header row. `RowMenu` IS the right primitive here (unlike the meal
         * card — DD9): this is a plain row with a natural tap action, so it has
         * an `onSelect` to give. The menu is omitted outright on an empty slot,
         * rather than disabled, so the row's own tap-to-log stays intact.
         */}
        {hasMeals ? (
          <RowMenu
            className={headerClassName}
            triggerPlacement="inline"
            onSelect={onHeaderPress}
            triggerTestID={`meal-slot-actions-${mealType.toLowerCase()}`}
            actionsLabel={t('copy.slotActionsLabel', { slot: label })}
            actions={[
              {
                key: 'copy',
                label: t('copy.slotAction', { slot: label }),
                icon: CopyIcon,
                systemImage: 'doc.on.doc',
                onPress: () => dispatchForLogs(logs, 'copy'),
                testID: 'meal-slot-copy',
              },
              {
                key: 'move',
                label: t('copy.slotMoveAction', { slot: label }),
                icon: CornerUpRight,
                systemImage: 'arrow.turn.up.right',
                onPress: () => dispatchForLogs(logs, 'move'),
                testID: 'meal-slot-move',
              },
            ]}
          >
            {headerContent}
          </RowMenu>
        ) : (
          <Pressable onPress={onHeaderPress} className={headerClassName}>
            {headerContent}
          </Pressable>
        )}

        {/* Expanded food items */}
        {isExpanded && hasMeals && (
          <View className="px-4 pb-4">
            {logs.map((log) => {
              const meal = mealsById.get(log.mealId);
              if (!meal) return null;
              const adj = MealUtils.adjustedNutrition(
                meal,
                log.consumedAmount ?? 0,
              );
              // Show the amount in the unit the user logged it in: a named
              // serving unit (e.g. "2 Package") when persisted, otherwise the
              // meal's base unit short sign (e.g. "2000g").
              const loggedUnit = log.consumedAmountUnit
                ? (meal.servingUnits ?? []).find(
                    (u) =>
                      u.name.toLowerCase() ===
                      log.consumedAmountUnit!.toLowerCase(),
                  )
                : undefined;
              const translatedUnit = meal.servingUnit
                ? t(`units.${meal.servingUnit}.shortSign`)
                : '';
              const amountLabel = !log.consumedAmount
                ? ''
                : loggedUnit
                  ? `${Math.round((log.consumedAmount / loggedUnit.weightGrams) * 100) / 100} ${loggedUnit.name}`
                  : translatedUnit
                    ? `${log.consumedAmount}${translatedUnit}`
                    : '';
              return (
                <Pressable
                  key={log.id}
                  onPress={() => {
                    const dateStr = formatISO(selectedDate, {
                      representation: 'date',
                    });
                    router.push({
                      pathname: `/meals/${dateStr}`,
                      params: { type: mealType },
                    });
                  }}
                  className="flex flex-row items-center justify-between border-t border-border py-3 active:opacity-70"
                >
                  <View className="flex-1 gap-px">
                    <Text
                      className="font-inter-medium text-text-primary"
                      style={{ fontSize: 13 }}
                      numberOfLines={1}
                    >
                      {meal.name || t('unnamedMeal')}
                    </Text>
                    {amountLabel ? (
                      <Text
                        className="font-inter-regular text-text-muted"
                        style={{ fontSize: 11 }}
                      >
                        {amountLabel}
                      </Text>
                    ) : null}
                  </View>
                  <View className="items-end gap-0.5">
                    <Text
                      className="font-inter-semibold text-text-primary"
                      style={{ fontSize: 12 }}
                    >
                      {Math.round(adj.calories ?? 0)} {t('nutrition.kcal.unit')}
                    </Text>
                    <Text
                      className="font-inter-regular text-text-muted"
                      style={{ fontSize: 11 }}
                    >
                      {formatMacroShort({
                        carbs: adj.carbs ?? 0,
                        protein: adj.protein ?? 0,
                        fats: adj.fats ?? 0,
                      })}
                    </Text>
                  </View>
                </Pressable>
              );
            })}

            {/* Log meal row */}
            <Pressable
              onPress={() => handleCreateMeal(mealType)}
              className="flex flex-row items-center justify-center gap-2 border-t border-border pt-3"
            >
              <Icon name="Plus" size={16} color={mutedColor} />
              <Text
                className="font-inter-medium text-text-muted"
                style={{ fontSize: 13 }}
              >
                {t('logMealAction')}
              </Text>
            </Pressable>
          </View>
        )}
      </View>
    );
  };

  return (
    <View className={className} {...props}>
      <View className="mb-3 flex flex-row items-center justify-between">
        <View className="flex flex-row items-center gap-3">
          <View className="h-0.5 w-6 rounded-sm bg-accent" />
          <Text className="font-inter-bold text-xs tracking-widest text-text-secondary uppercase">
            {t('title')}
          </Text>
        </View>
        <View className="flex flex-row items-center gap-3">
          <Pressable
            onPress={() => handleCreateMeal()}
            className="flex flex-row items-center gap-1.5"
          >
            <Icon name="Plus" size={14} color={accentColor} />
            <Text className="font-inter-medium text-xs text-accent">
              {t('addAction')}
            </Text>
          </Pressable>

          {/* Whole-day tier — omitted outright when the day holds no logs. */}
          {mealLogs.length > 0 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Pressable
                  testID="meal-day-actions"
                  collapsable={false}
                  accessibilityLabel={t('copy.dayActionsLabel')}
                  hitSlop={8}
                  className="h-7 w-7 items-center justify-center rounded-lg bg-surface-elevated active:opacity-70"
                >
                  <Icon name="Ellipsis" size={14} color={mutedColor} />
                </Pressable>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  testID="meal-day-copy"
                  collapsable={false}
                  onPress={() => dispatchForLogs(mealLogs, 'copy')}
                >
                  <UiIcon as={CopyIcon} size={16} className="text-foreground" />
                  <Text>{t('copy.dayAction')}</Text>
                </DropdownMenuItem>
                <DropdownMenuItem
                  testID="meal-day-move"
                  collapsable={false}
                  onPress={() => dispatchForLogs(mealLogs, 'move')}
                >
                  <UiIcon
                    as={CornerUpRight}
                    size={16}
                    className="text-foreground"
                  />
                  <Text>{t('copy.dayMoveAction')}</Text>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </View>
      </View>

      {isMobile ? (
        <View className="overflow-hidden rounded-xl bg-surface">
          {MOBILE_MEAL_TYPE_ORDER.map((mealType, index) => (
            <MealSlot
              key={mealType}
              mealType={mealType}
              showBorder={index < MOBILE_MEAL_TYPE_ORDER.length - 1}
            />
          ))}
        </View>
      ) : (
        <View className="flex flex-row overflow-hidden rounded-xl bg-surface">
          {/* Left column */}
          <View className="flex-1 border-r border-border">
            {LEFT_COLUMN_TYPES.map((mealType) => (
              <MealSlot key={mealType} mealType={mealType} />
            ))}
          </View>
          {/* Right column */}
          <View className="flex-1">
            {RIGHT_COLUMN_TYPES.map((mealType) => (
              <MealSlot key={mealType} mealType={mealType} />
            ))}
          </View>
        </View>
      )}
    </View>
  );
};
